import { Box, type BoxProps } from "@mui/material";
import { type MutableState, type Tools } from "final-form";
import arrayMutators from "final-form-arrays";
import { Form as ReactFinalFormForm, type FormProps as ReactFinalFormFormProps, FormSpy } from "react-final-form";
import { type OverrideProperties } from "type-fest";

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Custom array mutator to empty the array. Useful for cleanup functions */
function clear([name]: any[], state: MutableState<any>, { changeValue }: Tools<any>) {
  changeValue(state, name, (_values) => []);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export type FormBaseProps<FormValues = Record<PropertyKey, unknown>> = OverrideProperties<
  ReactFinalFormFormProps<FormValues>,
  { debug?: boolean }
>;
export interface FormSlotProps {
  slotProps?: { renderContainer?: BoxProps<"form"> };
}
export type FormProps<FormValues = Record<PropertyKey, unknown>> = FormBaseProps<FormValues> & FormSlotProps;

export function Form<FormValues>({ children, debug = false, onSubmit, slotProps, ...props }: FormProps<FormValues>) {
  return (
    <ReactFinalFormForm<FormValues>
      {...props}
      debug={function (state, fieldStates) {
        if (!debug || import.meta.env.MODE === "test") {
          return;
        }

        console.log(`(Form)(debug): `, { fieldStates, state });
      }}
      mutators={{ ...arrayMutators, clear, ...props.mutators }}
      onSubmit={async (values, form) => {
        if (form.getState().hasValidationErrors) {
          return;
        }

        try {
          await onSubmit(values, form);
        } catch (exception) {
          console.error(`(Form)(onSubmit): Error submitting form data.`);
          console.error({ data: values, ...form.getState() });
          console.error(JSON.stringify({ exception }, undefined, 2));
        }
      }}
      render={(renderProps) => {
        return (
          <>
            <Box noValidate {...slotProps?.renderContainer} component="form" onSubmit={renderProps.handleSubmit}>
              {typeof children === "function" ? children(renderProps) : children}
            </Box>
            {debug && import.meta.env.MODE !== "test" && (
              <FormSpy<FormValues>>
                {function WithFormSpy(props) {
                  return (
                    <pre style={{ backgroundColor: "#eee", marginTop: "2rem", padding: "2rem" }}>
                      <h4 style={{ marginTop: "0" }}>Form Values</h4>
                      {JSON.stringify(props.values, undefined, 2).replaceAll('\\"', "'")}
                    </pre>
                  );
                }}
              </FormSpy>
            )}
          </>
        );
      }}
    />
  );
}
